/**
 * Copyright © 2026 Adnuntius AS.
 */

// Static locale data for the formatting services. Derives the required data shape
// directly from the browser's Intl API, so no locale files need to be loaded.
export interface AngularLocaleData {
  id: string;
  DATETIME_FORMATS: {
    shortDate: string;
    DAY: string[];
    SHORTDAY: string[];
    MONTH: string[];
    SHORTMONTH: string[];
  };
  NUMBER_FORMATS: {
    GROUP_SEP: string;
    DECIMAL_SEP: string;
  };
}

const cache = new Map<string, AngularLocaleData>();

function buildMonthNames(locale: string, style: 'long' | 'short'): string[] {
  const formatter = new Intl.DateTimeFormat(locale, {month: style, timeZone: 'UTC'});
  const months: string[] = [];
  for (let month = 0; month < 12; month++) {
    months.push(formatter.format(new Date(Date.UTC(2021, month, 1))));
  }
  return months;
}

function buildDayNames(locale: string, style: 'long' | 'short'): string[] {
  const formatter = new Intl.DateTimeFormat(locale, {weekday: style, timeZone: 'UTC'});
  const days: string[] = [];
  // 2023-01-01 is a Sunday, so day 1-7 gives Sunday..Saturday, matching Angular's DAY/SHORTDAY order.
  for (let day = 1; day <= 7; day++) {
    days.push(formatter.format(new Date(Date.UTC(2023, 0, day))));
  }
  return days;
}

function buildShortDatePattern(locale: string): string {
  // A reference date with single-digit day/month exposes whether this locale zero-pads them,
  // and dateStyle: 'short' exposes the locale's own year-digit-count convention (matching
  // Angular's own single 'y' vs double 'yy' shortDate token choice).
  // dateStyle isn't in this project's configured TS lib target, though every supported browser implements it.
  const options = {dateStyle: 'short', timeZone: 'UTC'} as Intl.DateTimeFormatOptions;
  const parts = new Intl.DateTimeFormat(locale, options).formatToParts(new Date(Date.UTC(2023, 8, 4)));
  return parts.map((part) => {
    switch (part.type) {
      case 'day':
        return part.value.length >= 2 ? 'dd' : 'd';
      case 'month':
        return part.value.length >= 2 ? 'MM' : 'M';
      case 'year':
        return part.value.length >= 4 ? 'y' : 'yy';
      case 'literal':
        return part.value;
      default:
        return '';
    }
  }).join('');
}

function buildNumberSeparators(locale: string): {GROUP_SEP: string; DECIMAL_SEP: string} {
  const parts = new Intl.NumberFormat(locale).formatToParts(1234567.89);
  return {
    GROUP_SEP: parts.find((part) => part.type === 'group')?.value ?? ',',
    DECIMAL_SEP: parts.find((part) => part.type === 'decimal')?.value ?? '.'
  };
}

export function getIntlLocaleData(localeId: string): AngularLocaleData {
  const cached = cache.get(localeId);
  if (cached) {
    return cached;
  }
  const data: AngularLocaleData = {
    id: localeId,
    DATETIME_FORMATS: {
      shortDate: buildShortDatePattern(localeId),
      DAY: buildDayNames(localeId, 'long'),
      SHORTDAY: buildDayNames(localeId, 'short'),
      MONTH: buildMonthNames(localeId, 'long'),
      SHORTMONTH: buildMonthNames(localeId, 'short')
    },
    NUMBER_FORMATS: buildNumberSeparators(localeId)
  };
  cache.set(localeId, data);
  return data;
}
